// SEO utility — canonical URLs, JSON-LD schemas, keyword extraction
// Set REACT_APP_SITE_URL in .env.production to enable absolute canonical URLs
// e.g. REACT_APP_SITE_URL=https://www.kaushalprimenationnews.com

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '')
).replace(/\/$/, '');

export const PORTAL_LOGO_URL = `${SITE_URL}/logo.png`;
export const TWITTER_HANDLE = '@KPNation_';

export const PORTAL_DESCRIPTION_HI =
  'उत्तराखंड की आवाज़ — कौशल प्राइम नेशन। ताज़ा खबरें, राज्य समाचार, शिक्षा, नौकरी, तकनीक, स्वास्थ्य और राशिफल हिंदी में।';

export const PORTAL_DESCRIPTION_EN =
  'Voice of Uttarakhand — Kaushal Prime Nation. Latest news in Hindi and English covering national, state, education, jobs, technology, and health.';

// Returns an absolute canonical URL for a given path
export function getCanonicalUrl(path) {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return SITE_URL ? `${SITE_URL}${clean}` : clean;
}

// Organization JSON-LD schema (NewsMediaOrganization)
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    name: 'कौशल प्राइम नेशन',
    alternateName: 'Kaushal Prime Nation',
    url: SITE_URL || '/',
    logo: {
      '@type': 'ImageObject',
      url: PORTAL_LOGO_URL,
      width: 600,
      height: 60,
    },
    description: PORTAL_DESCRIPTION_EN,
    foundingDate: '2024',
    inLanguage: ['hi-IN', 'en-IN'],
    sameAs: [
      'https://www.youtube.com/@kaushalprimenation',
    ],
  };
}

// WebSite JSON-LD schema with SearchAction
export function generateWebsiteSchema() {
  const base = SITE_URL || '';
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Kaushal Prime Nation — कौशल प्राइम नेशन',
    alternateName: 'KPN News',
    url: `${base}/`,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${base}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// NewsArticle JSON-LD schema for article pages
export function generateNewsArticleSchema(article) {
  const url = getCanonicalUrl(`/article/${article.id}`);
  const imageUrl = article.image || PORTAL_LOGO_URL;
  const langCode = article.lang === 'en' ? 'en-IN' : 'hi-IN';
  const pubDate = article.pubDate
    ? new Date(article.pubDate).toISOString()
    : new Date().toISOString();

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: (article.title || '').slice(0, 110),
    description: (article.summary || article.title || '').slice(0, 300),
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
      width: 1200,
      height: 628,
    },
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    datePublished: pubDate,
    dateModified: pubDate,
    author: {
      '@type': 'Organization',
      name: 'KPN Editorial Team',
      url: SITE_URL || '/',
    },
    publisher: {
      '@type': 'NewsMediaOrganization',
      name: 'Kaushal Prime Nation',
      logo: {
        '@type': 'ImageObject',
        url: PORTAL_LOGO_URL,
        width: 600,
        height: 60,
      },
    },
    articleSection: article.category || 'News',
    inLanguage: langCode,
    isAccessibleForFree: true,
  };
}

// BreadcrumbList JSON-LD schema
// crumbs: [{ name: string, url?: string }]
export function generateBreadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => {
      const item = {
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
      };
      if (crumb.url) item.item = getCanonicalUrl(crumb.url);
      return item;
    }),
  };
}

// VideoObject JSON-LD schema for video content
export function generateVideoSchema({ title, description, thumbnailUrl, uploadDate, embedUrl }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title,
    description: description || title,
    thumbnailUrl: thumbnailUrl || PORTAL_LOGO_URL,
    uploadDate: uploadDate || new Date().toISOString(),
    embedUrl: embedUrl || '',
    publisher: {
      '@type': 'Organization',
      name: 'Kaushal Prime Nation',
      logo: { '@type': 'ImageObject', url: PORTAL_LOGO_URL },
    },
  };
}

// Extract comma-separated keywords from article data
export function extractKeywords(article, lang) {
  const langLabel = lang === 'en' ? 'news' : 'समाचार';
  const base = [
    'Kaushal Prime Nation',
    'KPN',
    'कौशल प्राइम नेशन',
    langLabel,
    article.category || '',
    article.state || '',
  ].filter(Boolean);

  const titleWords = (article.title || '')
    .split(/[\s,।]+/)
    .filter(w => w.length > 3)
    .slice(0, 6);

  return [...new Set([...base, ...titleWords])].join(', ');
}

// Returns OG-ready page metadata for known category slugs
export function getCategoryOgMeta(slug, lang) {
  const meta = {
    national:   { hi: 'देश की खबरें — राजनीति, संसद और भारत की ताज़ा खबरें।',   en: 'Latest national news from India — politics, parliament, defence and more.' },
    world:      { hi: 'दुनिया की ताज़ा खबरें — अमेरिका, चीन, पाकिस्तान, रूस, यूरोप।', en: 'International news — USA, China, Pakistan, Russia, Europe and beyond.' },
    education:  { hi: 'शिक्षा समाचार — बोर्ड परीक्षा, NEET, JEE, UPSC, छात्रवृत्ति।', en: 'Education news — board exams, NEET, JEE, UPSC, scholarships and admissions.' },
    jobs:       { hi: 'नौकरी समाचार — सरकारी भर्ती, UPSC, SSC, रेलवे, बैंक PO वैकेंसी।', en: 'Jobs & recruitment news — government vacancies, UPSC, SSC, Railway, Bank PO.' },
    health:     { hi: 'स्वास्थ्य समाचार — बीमारी, उपचार, योग, आयुर्वेद और WHO अपडेट।', en: 'Health news — disease, treatment, yoga, Ayurveda and WHO updates.' },
    technology: { hi: 'तकनीक समाचार — AI, स्मार्टफोन, साइबर सुरक्षा, ISRO, स्टार्टअप।', en: 'Technology news — AI, smartphones, cybersecurity, ISRO, startups.' },
    astro:      { hi: 'दैनिक राशिफल, वैदिक ज्योतिष, कुंडली और ग्रह दशा।',        en: 'Daily horoscope, Vedic astrology, kundali and planetary positions.' },
    rental:     { hi: 'मकान, फ्लैट, कमरा, दुकान और ऑफिस किराये की सभी ताज़ी खबरें।', en: 'Rental news — apartments, houses, rooms, commercial spaces.' },
    quiz:       { hi: 'सामान्य ज्ञान, करंट अफेयर्स, विज्ञान और इतिहास प्रश्नोत्तरी।', en: 'Quiz & GK — general knowledge, current affairs, science and history quizzes.' },
    podcast:    { hi: 'पॉडकास्ट — ताज़ी खबरें, विश्लेषण और विशेष चर्चाएं सुनें।',  en: 'Podcasts — listen to latest news, analysis and special discussions.' },
  };
  const m = meta[slug];
  if (!m) return lang === 'EN' ? `${slug} news — Kaushal Prime Nation` : `${slug} समाचार — कौशल प्राइम नेशन`;
  return lang === 'EN' ? m.en : m.hi;
}
